import React, { useContext } from 'react'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary
} from '@clientbase/clientbase-library'
import { Box, Typography } from '@mui/material'
import {
  amountTypeEnum,
  issueWhenEnum,
  natureOperationEnum
} from 'models/Invoice'
import moment from 'moment'
import InvoiceIcon from 'templates/Invoice/components/InvoiceIcon'

import { cardTittle } from 'components/Modal/ModalChargeDetail/ModalChargeDetail.utils'

import { formatMoney } from 'utils'

import { theme } from 'styles/theme'

import { ModalInstallmentDetailContext } from '../../context'
import * as S from './NFe.styles'

const nfseInstallmentScopeEnum: Record<string, string> = {
  per_installment: 'Valor da parcela (por mês)',
  per_cycle: 'Valor total do parcelamento'
}

const nfsePolicyEnum: Record<string, string> = {
  no_nfse: 'Sem emissão de nota fiscal',
  on_billing_issued: 'Quando a fatura abrir para pagamento',
  on_billing_paid: 'Quando o pagamento for efetuado',
  on_demand: 'Emissão manual',
  on_before_due_date: 'Antes do vencimento',
  on_after_due_date: 'Depois do vencimento',
  custom_schedule: 'Data específica'
}

const NFe: React.FC = () => {
  const { installment } = useContext(ModalInstallmentDetailContext)

  if (!installment?.nfse_scheduled) {
    return null
  }

  const nfseScheduled = installment.nfse_scheduled

  return (
    <Accordion
      sx={{
        border: '1px solid #E8EBE8',
        borderLeft: '3px solid',
        borderLeftColor: 'primary.main',
        borderRadius: '8px !important',
        backgroundColor: '#FAFBFA',
        boxShadow: 'none',
        '&:before': { display: 'none' },
        '&.Mui-expanded': {
          margin: 0,
          backgroundColor: '#fff'
        }
      }}
    >
      <AccordionSummary aria-controls="nfse-content" id="nfse-header">
        {cardTittle(
          'Nota fiscal',
          <InvoiceIcon />,
          `${theme.palette.primary.main}`
        )}
      </AccordionSummary>
      <AccordionDetails>
        <>
          <Box
            display="flex"
            gap={1.25}
            alignItems="end"
            margin="10px 0px 10px 0px"
          >
            <Typography variant="smLight" color="#777777">
              Prestador de serviço:
            </Typography>
            <S.Separator />
            <Typography variant="smBold">
              {nfseScheduled?.nfse_issuer?.legal_name || ''}
            </Typography>
          </Box>
          <Box
            display="flex"
            gap={1.25}
            alignItems="end"
            margin={'10px 0px 10px 0px'}
          >
            <Typography variant="smLight" color="#777777">
              ISS retido:
            </Typography>
            <S.Separator />
            <Typography variant="smBold">
              {nfseScheduled?.iss_retention === true ? 'Sim' : 'Não'}
            </Typography>
          </Box>
          <Box
            display="flex"
            gap={1.25}
            alignItems="end"
            margin={'10px 0px 10px 0px'}
          >
            <Typography variant="smLight" color="#777777">
              Status:
            </Typography>
            <S.Separator />
            <Typography variant="smBold">
              {nfseScheduled?.nfse_uuid ? 'Emitida' : 'Agendada'}
            </Typography>
          </Box>
          {!nfseScheduled?.nfse_uuid && nfseScheduled?.nfse_policy && (
            <Box
              display="flex"
              gap={1.25}
              alignItems="end"
              margin={'10px 0px 10px 0px'}
            >
              <Typography variant="smLight" color="#777777">
                Quando será emitida:
              </Typography>
              <S.Separator />
              <Typography variant="smBold">
                {nfsePolicyEnum[nfseScheduled?.nfse_policy as string] ||
                  issueWhenEnum[
                    nfseScheduled?.nfse_policy as keyof typeof issueWhenEnum
                  ] ||
                  nfseScheduled?.nfse_policy}
              </Typography>
            </Box>
          )}
          {nfseScheduled?.nfse_policy === 'custom_schedule' &&
            nfseScheduled?.issue_date && (
              <Box
                display="flex"
                gap={1.25}
                alignItems="end"
                margin={'10px 0px 10px 0px'}
              >
                <Typography variant="smLight" color="#777777">
                  Data de emissão:
                </Typography>
                <S.Separator />
                <Typography variant="smBold">
                  {moment(nfseScheduled?.issue_date).format('DD/MM/YYYY')}
                </Typography>
              </Box>
            )}
          {((nfseScheduled as any)?.configuration_type === 1 ||
            (nfseScheduled as any)?.configuration_type ===
              'specific_day_of_month' ||
            (nfseScheduled as any)?.issue_mode === 'specific_day') && (
            <>
              <Box
                display="flex"
                gap={1.25}
                alignItems="end"
                margin={'10px 0px 10px 0px'}
              >
                <Typography variant="smLight" color="#777777">
                  Dia do mês:
                </Typography>
                <S.Separator />
                <Typography variant="smBold">
                  {(nfseScheduled as any)?.day_of_month ||
                    (nfseScheduled as any)?.specific_day ||
                    ''}
                </Typography>
              </Box>
              <Box
                display="flex"
                gap={1.25}
                alignItems="end"
                margin={'10px 0px 10px 0px'}
              >
                <Typography variant="smLight" color="#777777">
                  Mês de emissão:
                </Typography>
                <S.Separator />
                <Typography variant="smBold">
                  {(() => {
                    const relVal = (nfseScheduled as any)
                      ?.due_date_relation as string
                    const mapStr: Record<string, string> = {
                      two_months_before: 'Dois meses antes do vencimento',
                      month_before: 'Mês anterior ao vencimento',
                      same_month: 'Mês do vencimento',
                      month_after: 'Mês seguinte ao vencimento',
                      two_months_after: 'Dois meses depois do vencimento'
                    }
                    if (relVal) return mapStr[relVal] || ''
                    const off = (nfseScheduled as any)?.specific_month_offset
                    const offMap: Record<string, string> = {
                      '-2': mapStr.two_months_before,
                      '-1': mapStr.month_before,
                      '0': mapStr.same_month,
                      '1': mapStr.month_after,
                      '2': mapStr.two_months_after
                    }
                    return offMap[String(off)] || ''
                  })()}
                </Typography>
              </Box>
            </>
          )}

          <Box
            display="flex"
            gap={1.25}
            alignItems="end"
            margin={'10px 0px 10px 0px'}
          >
            <Typography variant="smLight" color="#777777">
              Item de serviço:
            </Typography>
            <S.Separator />
            <Typography variant="smBold">
              {nfseScheduled?.nfse_issuer_service?.code} -{' '}
              {nfseScheduled?.nfse_issuer_service?.description}
            </Typography>
          </Box>
          <Box
            display="flex"
            gap={1.25}
            alignItems="end"
            margin={'10px 0px 10px 0px'}
          >
            <Typography variant="smLight" color="#777777">
              Valor da nota:
            </Typography>
            <S.Separator />
            <Typography variant="smBold">
              {amountTypeEnum[nfseScheduled?.amount_type as string] || ''}{' '}
              {nfseScheduled?.amount
                ? `- ${formatMoney(Number(nfseScheduled?.amount))}`
                : ''}
            </Typography>
          </Box>
          {nfseScheduled?.nfse_installment_scope && (
            <Box
              display="flex"
              gap={1.25}
              alignItems="end"
              margin={'10px 0px 10px 0px'}
            >
              <Typography variant="smLight" color="#777777">
                Tipo de emissão:
              </Typography>
              <S.Separator />
              <Typography variant="smBold">
                {nfseInstallmentScopeEnum[
                  nfseScheduled?.nfse_installment_scope as string
                ] || nfseScheduled?.nfse_installment_scope}
              </Typography>
            </Box>
          )}
          {nfseScheduled?.nature_operation && (
            <Box
              display="flex"
              gap={1.25}
              alignItems="end"
              margin={'10px 0px 10px 0px'}
            >
              <Typography variant="smLight" color="#777777">
                Natureza de operação:
              </Typography>
              <S.Separator sx={{ marginTop: '4px' }} />
              <Typography variant="smBold">
                {natureOperationEnum[nfseScheduled?.nature_operation as string]}
              </Typography>
            </Box>
          )}

          {nfseScheduled?.copy_description ? (
            <Box
              display="flex"
              gap={1.25}
              alignItems="end"
              margin={'10px 0px 10px 0px'}
            >
              <Typography variant="smLight" color="#777777">
                Copiar descrição da cobrança
              </Typography>
              <S.Separator />
              <Typography variant="smBold">
                {nfseScheduled?.copy_description ? 'Sim' : 'Não'}
              </Typography>
            </Box>
          ) : (
            nfseScheduled?.description && (
              <Box display="flex" flexDirection="column">
                <Typography variant="smLight" color="#777777" mb="6px">
                  Descrição:
                </Typography>
                <Typography variant="smBold">
                  {nfseScheduled?.description}
                </Typography>
              </Box>
            )
          )}
        </>
      </AccordionDetails>
    </Accordion>
  )
}

export default NFe
